import { memo, useEffect, useMemo, useState } from 'react';

import { Collapse, Pagination, Spin, Typography } from 'antd';

import type IPagination from 'interfaces/IPagination';
import { Mask } from 'v2/components/Shared/ValuesVisibility';
import { numberToBRL } from 'v2/helpers/money';
import { EFinancialStatementTabs } from 'v2/interfaces/financial';
import type { IFinancialEntry, SomeFinancialStatementTabs } from 'v2/interfaces/financial';
import { InitialPagination } from 'v2/services/financialStatement/statement';

import { Icon } from '@eduzz/ui-antd-theme';

import ContentInnerTable from './ContentInnerTable';

interface IProps {
  id: string;
  data: IFinancialEntry[];
  pagination: IPagination;
  loading: boolean;
  handlePagination: {
    onChangeRowsPerPage: (per_page: number) => void;
    onChangePage: (page: number) => void;
  };
  balanceLabel: string;
  showBlockedDetails?: boolean;
  currentTab?: SomeFinancialStatementTabs;
}

const formatDate = (dateRaw: string) => new Intl.DateTimeFormat('pt-BR').format(new Date(`${dateRaw}T00:00:00`));

function renderPanelLabel(
  entry: IFinancialEntry,
  balanceLabel: string,
  showBlockedDetails?: boolean,
  currentTab?: SomeFinancialStatementTabs
) {
  return (
    <div className='flex min-w-0 grow flex-wrap items-center gap-x-4 gap-y-1 max-sm:flex-col max-sm:items-start'>
      <div className='flex min-w-0 flex-wrap items-baseline gap-x-2'>
        <Typography.Text>
          {showBlockedDetails && 'Bloqueio em '}
          {formatDate(entry.transaction_date)}
        </Typography.Text>
        <Typography.Text type='secondary'>{`${entry.items} registros`}</Typography.Text>
      </div>

      <div className='ml-auto flex flex-wrap items-center gap-x-6 gap-y-1 max-sm:ml-0'>
        {currentTab === EFinancialStatementTabs.STATEMENT && (
          <>
            <div>
              <Typography.Text type='secondary' className='pr-2'>
                Créditos
              </Typography.Text>
              <Typography.Text>
                <Mask>{typeof entry?.positive === 'number' ? numberToBRL(entry?.positive) : 'R$ -'}</Mask>
              </Typography.Text>
            </div>
            <div>
              <Typography.Text type='secondary' className='pr-2'>
                Débitos
              </Typography.Text>
              <Typography.Text type={typeof entry?.negative === 'number' && entry.negative < 0 ? 'danger' : undefined}>
                <Mask>{typeof entry?.negative === 'number' ? numberToBRL(entry?.negative) : 'R$ -'}</Mask>
              </Typography.Text>
            </div>
          </>
        )}
        <div>
          <Typography.Text type='secondary' className='pr-2'>
            {balanceLabel}
          </Typography.Text>
          <Typography.Text>
            <Mask>{numberToBRL(entry.balance)}</Mask>
          </Typography.Text>
        </div>
      </div>
    </div>
  );
}

function ContentTable({
  id,
  data,
  pagination,
  loading,
  handlePagination,
  balanceLabel,
  showBlockedDetails,
  currentTab
}: IProps) {
  const [activeKey, setActiveKey] = useState<string | null>(null);

  useEffect(() => {
    if (!loading && data?.length) {
      setActiveKey(data[0].transaction_date);
      return;
    }

    setActiveKey(null);
  }, [data, loading]);

  const items = useMemo(
    () =>
      data?.map(entry => ({
        key: entry.transaction_date,
        label: renderPanelLabel(entry, balanceLabel, showBlockedDetails, currentTab),
        children: (
          <ContentInnerTable transactionDate={entry.transaction_date} showBlockedDetails={showBlockedDetails} />
        ),
        classNames: {
          header:
            'items-center bg-(--uizz-antd-color-bg-container) px-4 py-3 hover:bg-(--uizz-antd-color-fill-quaternary)',
          body: 'px-4 pb-3 max-sm:px-2'
        }
      })) || [],
    [data, balanceLabel, showBlockedDetails, currentTab]
  );

  return (
    <div>
      {loading && (
        <div className='flex justify-center py-4'>
          <Spin />
        </div>
      )}
      <div id={id}>
        {!!data?.length && (
          <Collapse
            accordion
            destroyOnHidden
            expandIconPosition='end'
            size='small'
            activeKey={activeKey ?? undefined}
            onChange={keys => setActiveKey(keys[0] || null)}
            expandIcon={({ isActive }) => (
              <Icon.ChevronDown size={16} className={`transition-transform ${isActive ? 'rotate-180' : ''}`} />
            )}
            className='overflow-hidden border-(--uizz-antd-color-split) [--uizz-antd-color-border:var(--uizz-antd-color-split)]'
            items={items}
          />
        )}
      </div>

      {!loading && (pagination?.total_pages > 1 || pagination?.per_page > InitialPagination.per_page) && (
        <div className='mt-4 flex justify-end'>
          <Pagination
            total={pagination?.total_items || 0}
            current={pagination?.current_page || 1}
            pageSize={pagination?.per_page || 15}
            pageSizeOptions={[15, 30, 50, 100, 200]}
            showSizeChanger
            onChange={page => handlePagination.onChangePage(page)}
            onShowSizeChange={(_, size) => handlePagination.onChangeRowsPerPage(size)}
          />
        </div>
      )}
    </div>
  );
}

export default memo(ContentTable);
